import { Icon } from "@hoylu/client-common";
import { UserSettingsContext } from "@hoylu/user-settings";
import React, { type CSSProperties, useContext } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "typesafe-actions";
import { selectAnalyticsWorkspaces } from "../../../state/analytics/analytics.selector";
import { enableFavoritesWorkspaceSection } from "../../../state/config/config.selector";
import type { MyWorkspaceMetadata } from "../../../state/workspaces/types";
import { isWorkspaceLimited } from "../../../state/workspaces/workspaces.selector";
import { Localized } from "../../../strings";
import { WorkspaceWeight } from "../WorkspaceWeight";
import { WorkspaceCardContext } from "./context/workspace.card.context";
import Styles from "./WorkspaceImagePreview.module.css";
import WorkspacePreviewLabels from "./WorkspacePreviewLabels";

type WorkspaceImagePreviewProps = {
  canEditLabels: boolean;
  imgSrc: string;
  showEditLabelPrompt: () => void;
  showWorkspaceWeight: boolean;
  myWorkspaceMetadata?: MyWorkspaceMetadata;
};

const cardIconStyles: CSSProperties = {
  position: "absolute",
  top: "0",
  fontSize: "var(--global--size-large)",
  // We have to reset the Icon's default paddings
  padding: "0",
};

const WorkspaceImagePreview = ({
  canEditLabels,
  imgSrc,
  myWorkspaceMetadata,
  showEditLabelPrompt,
  showWorkspaceWeight,
}: WorkspaceImagePreviewProps) => {
  const cardContext = useContext(WorkspaceCardContext);
  const userSettings = useContext(UserSettingsContext);

  const isWorkspaceFavorite = cardContext.isFavorite;

  const isLimited = useSelector((state: RootState) =>
    isWorkspaceLimited(state, myWorkspaceMetadata?.workspaceId),
  );
  const showFavoritesStar = useSelector(enableFavoritesWorkspaceSection);
  const nexusWorkspaces = useSelector(selectAnalyticsWorkspaces);
  const showNexusLabelIcon =
    !!myWorkspaceMetadata?.workspaceId &&
    nexusWorkspaces.includes(myWorkspaceMetadata.workspaceId);

  const handleFavoriteClick = () => {
    myWorkspaceMetadata &&
      userSettings?.updateFavoriteWorkspaces(myWorkspaceMetadata.workspaceId);
  };

  return (
    <>
      <img
        alt={Localized.string("WORKSPACE_CARD.WORKSPACE_IMG_ALT")}
        className={Styles.workspaceImage}
        src={imgSrc}
      />
      {showFavoritesStar && (
        <Icon
          icon={
            isWorkspaceFavorite
              ? "hoylu-ui-icons-star-filled"
              : "hoylu-ui-icons-star"
          }
          onClick={handleFavoriteClick}
          style={{
            ...cardIconStyles,
            color: isWorkspaceFavorite ? "#fff" : "",
            right: "var(--global--spacing--default)",
          }}
        />
      )}
      <WorkspacePreviewLabels
        canEditLabels={canEditLabels}
        myWorkspaceMetadata={myWorkspaceMetadata!}
        showEditLabelPrompt={showEditLabelPrompt}
      />
      {showNexusLabelIcon && (
        <Icon
          icon="hoylu-ui-icons-nexus"
          style={{
            ...cardIconStyles,
            left: "var(--global--size-large)",
          }}
        />
      )}
      {showWorkspaceWeight && (
        <WorkspaceWeight
          inWarningMode={isLimited}
          workspaceWeight={myWorkspaceMetadata?.weight}
        />
      )}
    </>
  );
};

export default WorkspaceImagePreview;
